import * as React from "react";
import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";

export type ThemeVersion = "v1" | "v2";

const STORAGE_KEY = "finster_theme_version";

function readSavedVersion(): ThemeVersion {
  if (typeof localStorage === "undefined") return "v2";
  return localStorage.getItem(STORAGE_KEY) === "v1" ? "v1" : "v2";
}

function applyDocumentTheme(version: ThemeVersion): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.toggle("design-v2", version === "v2");
  root.classList.toggle("design-v1", version === "v1");

  document.title = "FactSet";
  const favicon = document.getElementById("favicon") as HTMLLinkElement | null;
  if (favicon) {
    favicon.type = "image/png";
    favicon.href = "assets/v2/icon-32.png";
  }
}

export type HeaderLogo =
  | { variant: "factset"; deviceSrc: string; fullSrc: string; title: string; alt: string }
  | { variant: "st4"; fullSrc: string; alt: string };

interface ThemeContextValue {
  themeVersion: ThemeVersion;
  setThemeVersion: (version: ThemeVersion) => void;
  isSt4: boolean;
  brandName: string;
  /** Small mark for inline / review states */
  deviceLogoSrc: string;
  headerLogo: HeaderLogo;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function buildContextValue(version: ThemeVersion): ThemeContextValue {
  if (version === "v2") {
    return {
      themeVersion: version,
      setThemeVersion: () => {},
      isSt4: true,
      brandName: "FactSet",
      deviceLogoSrc: "assets/v2/logo-device.svg",
      headerLogo: {
        variant: "st4",
        fullSrc: "assets/v2/logo-full.svg",
        alt: "FactSet",
      },
    };
  }
  return {
    themeVersion: version,
    setThemeVersion: () => {},
    isSt4: false,
    brandName: "FactSet",
    deviceLogoSrc: "assets/v2/logo-device.svg",
    headerLogo: {
      variant: "factset",
      deviceSrc: "assets/v2/logo-device.svg",
      fullSrc: "assets/v2/logo-full.svg",
      title: "FactSet",
      alt: "FactSet",
    },
  };
}

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [themeVersion, setThemeVersionState] = useState<ThemeVersion>(readSavedVersion);

  const setThemeVersion = useCallback((version: ThemeVersion) => {
    setThemeVersionState(version);
    localStorage.setItem(STORAGE_KEY, version);
    applyDocumentTheme(version);
  }, []);

  useEffect(() => {
    applyDocumentTheme(themeVersion);
  }, [themeVersion]);

  const base = buildContextValue(themeVersion);
  const value: ThemeContextValue = {
    ...base,
    setThemeVersion,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    const fallback = buildContextValue("v1");
    return { ...fallback, setThemeVersion: () => {} };
  }
  return ctx;
}
